import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { Run, Series } from "../lib/supabase";
import RunForm from "../components/runs/RunForm";
import SmartBackButton from "../components/ui/SmartBackButton";
import toast from "react-hot-toast";
import { ENTITY_NAMES } from "../constants/entities";
import LoadingScreen from "../components/ui/LoadingScreen";
import { normalizeDaysOfWeek } from "../utils/runs";

const AddRun: React.FC = () => {
  const { seriesId } = useParams<{ seriesId: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const [series, setSeries] = useState<Series | null>(null);
  const [isFetching, setIsFetching] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSeries = async () => {
      if (!seriesId) return;
      try {
        const { data, error } = await supabase
          .from("series")
          .select("*")
          .eq("id", seriesId)
          .single();

        if (error) throw error;
        setSeries(data);
      } catch (error) {
        console.error("Error fetching series:", error);
        toast.error("Failed to fetch series details");
      } finally {
        setIsFetching(false);
      }
    };

    fetchSeries();
  }, [seriesId]);

  const handleCreateRun = async (data: Partial<Run>) => {
    try {
      if (!seriesId) return;
      setIsLoading(true);

      const { error } = await supabase.from("runs").insert({
        series_id: seriesId,
        name: data.name || "",
        day_of_week: normalizeDaysOfWeek(data.day_of_week || []),
        start_time: data.start_time || "",
        end_time: data.end_time || "",
        start_date: data.start_date || "",
        location: data.location || "",
        instructor: data.instructor || "",
        max_students: data.max_students || 20,
        color: data.color || "#3B82F6",
        price: data.price || 0,
        active: data.active ?? true,
      });

      if (error) throw error;

      toast.success(`${ENTITY_NAMES.RUN} created successfully`);
      navigate(`/series/${seriesId}`);
    } catch (error) {
      console.error("Error creating run:", error);
      // Log the full error details for debugging
      if (error && typeof error === "object" && "message" in error) {
        console.error("Error message:", error.message);
      }
      toast.error(`Failed to create ${ENTITY_NAMES.RUN.toLowerCase()}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) return <LoadingScreen />;

  if (!series) {
    return (
      <div className="p-8 text-center">
        <h2 className="text-xl font-bold mb-2">Series Not Found</h2>
        <SmartBackButton fallbackPath="/series" className="btn btn-primary" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center mb-6">
        <SmartBackButton
          fallbackPath={`/series/${seriesId}`}
          className="text-link hover:text-hover flex items-center"
        />
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <h1>Add Run to {series.code}</h1>
          </div>

          <RunForm
            onSubmit={handleCreateRun}
            isLoading={isLoading}
            seriesId={seriesId || ""}
            onCancel={() => navigate(`/series/${seriesId}`)}
          />
        </div>
      </div>
    </div>
  );
};

export default AddRun;
